'use strict';
import IconButton from '@components/Common/IconButton.js';
import Search from '@components/Common/Search.js';
import Loader from '@components/Loading/Loader.js';
import ProjectsState, {
  AiSelectedItems,
  LoadedFolderIds,
} from '@states/Projects.js';
import { UIButton, UICheckbox, UIDiv, UIText } from '@ui';

class AiAnalyzerProjectsPopover extends UIDiv {
  constructor() {
    super();
    this.addClass('w-96 h-[450px] rounded-lg overflow-hidden flex flex-col');
    this.searchTerm = '';
    this.isBuilding = false;

    this.projects = ProjectsState.projects;

    // Navigation state for projects tab
    this.projectsView = 'list'; // 'list' or 'tasks'
    this.currentProject = null; // Currently viewed project for task navigation

    ProjectsState.subscribe(this.#buildUI.bind(this));

    AiSelectedItems.subscribe(() => {
      this.#buildUI(); // Rebuild UI to reflect selection changes - this updates counter and header
    });

    // Initialize view based on current selections
    this.initializeViewFromSelections();
  }

  initializeViewFromSelections() {
    const selectedItems = AiSelectedItems.selectedItems;
    if (
      selectedItems.projects.length === 0 &&
      selectedItems.tasks.length === 0
    ) {
      // No selections, start with project list
      this.projectsView = 'list';
      this.currentProject = null;
      this.#buildUI(); // Refresh the UI
      return;
    }

    // Check if we have projects data
    if (!this.projects || this.projects.isLoading) {
      this.projectsView = 'list';
      this.currentProject = null;
      this.#buildUI(); // Refresh the UI
      return;
    }

    const folders = this.projects?.folders || [];
    const tasks = this.projects?.tasks || [];

    // Check if selected items are tasks
    const selectedTasks = selectedItems.tasks.filter(id =>
      tasks.some(task => task.id === id),
    );

    if (selectedTasks.length > 0) {
      // Find the parent project of the first selected task
      const firstTask = tasks.find(task => task.id === selectedTasks[0]);
      if (firstTask) {
        const parentProject = folders.find(
          folder => folder.id === firstTask.parentId,
        );
        if (parentProject) {
          // Navigate to tasks view of the parent project
          this.currentProject = {
            id: parentProject.id,
            name: parentProject.name,
            type: 'folder',
          };
          this.projectsView = 'tasks';
          this.#buildUI(); // Refresh the UI
          return;
        }
      }
    }

    // Default to project list view
    this.projectsView = 'list';
    this.currentProject = null;
    this.#buildUI(); // Refresh the UI
  }

  #canSelectItem(itemId, itemType) {
    const currentSelection = AiSelectedItems.selectedItems;

    if (
      currentSelection.projects.length === 0 &&
      currentSelection.tasks.length === 0
    ) {
      // No current selection, can select anything
      return true;
    }

    if (!this.projects || this.projects.isLoading) {
      return false;
    }

    const folders = this.projects?.folders || [];
    const tasks = this.projects?.tasks || [];

    // Check what type of items are currently selected
    const selectedProjects = currentSelection.projects || [];
    const selectedTasks = currentSelection.tasks || [];

    if (itemType === 'folder') {
      // Trying to select a project
      if (selectedTasks.length > 0) {
        // Clear current selection and allow this selection
        AiSelectedItems.setSelectedItems({ projects: [], tasks: [] });
        return true;
      }
      return true;
    } else if (itemType === 'task') {
      // Trying to select a task
      if (selectedProjects.length > 0) {
        // Clear current selection and allow this selection
        AiSelectedItems.setSelectedItems({ projects: [], tasks: [] });
        return true;
      }

      // If other tasks are selected, check if they're from the same project
      if (selectedTasks.length > 0) {
        const newTask = tasks.find(task => task.id === itemId);
        const firstSelectedTask = tasks.find(
          task => task.id === selectedTasks[0],
        );

        if (!newTask || !firstSelectedTask) {
          return false;
        }

        // If tasks are from different projects, clear current selection
        if (newTask.parentId !== firstSelectedTask.parentId) {
          AiSelectedItems.setSelectedItems({ projects: [], tasks: [] });
          return true;
        }
      }

      return true;
    }

    return false;
  }

  #updateProjectsSelectionCounter(deselectAll = false) {
    // Find and update the projects selection counter
    const selectedItems = AiSelectedItems.selectedItems;
    const selectedCount = deselectAll
      ? 0
      : selectedItems.projects.length + selectedItems.tasks.length;
    const selectionText = i18next.t('sidebar.aiAnalyzer.projects_selected', {
      count: selectedCount,
    });

    // Find the counter element in the projects section and update it
    const counterElement = this.listContainer.find(
      '.projects-selection-counter',
    )[0];
    if (counterElement) {
      counterElement.setTextContent(selectionText);
    }
  }

  #buildUI() {
    if (this.isBuilding) return;
    this.isBuilding = true;

    // Update projects reference from state
    this.projects = ProjectsState.projects;

    this.clear();

    // Header
    const header = this.#buildHeader();
    this.add(header);

    // Files list container with scrolling
    this.listContainer = new UIDiv();
    this.listContainer.addClass('p-2 space-y-2 overflow-y-auto flex-1');
    this.add(this.listContainer);

    this.#buildList();
    this.isBuilding = false;
  }

  #buildList() {
    this.listContainer.clear();

    // Projects navigation - show either project list or task list
    if (this.projectsView === 'list') {
      this.#buildProjectsList();
    } else if (this.projectsView === 'tasks') {
      this.#buildTasksList();
    }
  }

  #buildHeader() {
    const header = new UIDiv();
    header.addClass(
      'sticky top-0 bg-white border-b border-gray-200 p-2 flex-shrink-0',
    );

    const search = new Search({
      handleSearch: searchValue => {
        this.searchTerm = searchValue.toLowerCase();
        this.#buildList(); // This will call the appropriate method based on projectsView
      },
      placeholder: `${i18next.t('common.search')}...`,
    });

    header.add(search);

    // Selection count and deselect all
    const isTasksView = this.projectsView === 'tasks';
    const projectsHeader = new UIDiv();
    projectsHeader.addClass(
      `flex items-center justify-between p-2 pb-0${isTasksView ? ' border-b border-gray-100 pb-2' : ''}`,
    );

    // Selection counter
    const selectedItems = AiSelectedItems.selectedItems;
    const selectedCount =
      selectedItems.projects.length + selectedItems.tasks.length;
    const selectionIndicator = new UIText(`${selectedCount} selections`);
    selectionIndicator.addClass(
      'projects-selection-counter font-medium text-sm text-slate-700',
    );
    projectsHeader.add(selectionIndicator);

    // Deselect all button
    const deselectAllBtn = new UIButton(
      i18next.t('sidebar.aiAnalyzer.deselect_all'),
    );
    deselectAllBtn.addClass(
      'text-sm text-slate-500 hover:text-slate-700 px-1.5 py-1 hover:bg-slate-100 rounded-md transition-colors',
    );
    deselectAllBtn.onClick(e => {
      e.stopPropagation();
      // Update all selected items visually
      const allProjectItems = this.listContainer.find('[id^="project-item-"]');
      allProjectItems.forEach(item => {
        // unselect the checkbox
        const checkbox = item.find('input[type="checkbox"]')[0];
        if (checkbox && checkbox.getChecked()) {
          checkbox.setChecked(false);
        }
      });

      AiSelectedItems.setSelectedItems({ projects: [], tasks: [] });
      // Rebuild UI to update header and counter
      this.#buildUI();
    });
    projectsHeader.add(deselectAllBtn);
    header.add(projectsHeader);

    // Tasks header for tasks view
    if (isTasksView) {
      const tasksHeader = new UIDiv();
      tasksHeader.addClass('flex items-center gap-2');

      // Back button
      const backButton = new IconButton({
        iconName: 'arrow-left',
        color: 'none',
        text: '',
        iconSize: 18,
        onClick: e => {
          e.stopPropagation();
          this.#navigateBackToProjects();
        },
        buttonClass: '!px-1',
      });

      tasksHeader.add(backButton);

      // Project name
      const projectNameDiv = new UIDiv();
      projectNameDiv.addClass('flex-1');
      const projectName = new UIText(this.currentProject.name);
      projectName.addClass('text-sm font-medium text-slate-700');
      projectNameDiv.add(projectName);
      tasksHeader.add(projectNameDiv);
      header.add(tasksHeader);
    }

    return header;
  }

  #buildProjectsList() {
    // Check if projects are still loading or not available
    if (!this.projects || this.projects.isLoading) {
      const loadingSection = new UIDiv();
      loadingSection.addClass('space-y-1 mb-4');
      const loadingMessage = new UIText(i18next.t('common.loading'));
      loadingMessage.addClass('text-sm text-slate-500 px-2 py-1');
      loadingSection.add(loadingMessage);

      this.listContainer.add(loadingSection);
      return;
    }

    // Get all projects (folders)
    const folders = this.projects?.folders || [];
    const tasks = this.projects?.tasks || [];
    const loadedFolderIds = LoadedFolderIds.loadedFolderIds || [];

    // Create project data with task counts
    const projectsData = [];

    // Add folders as projects
    folders.forEach(folder => {
      const isLoaded = loadedFolderIds.includes(folder.id);
      const folderTasks = tasks.filter(task => task.parentId === folder.id);
      projectsData.push({
        id: folder.id,
        name: folder.name,
        type: 'folder',
        // Only show task count if the project's tasks have been fetched
        taskCount: isLoaded ? folderTasks.length : null,
        createdAt: folder.createdAt,
        isSelected: AiSelectedItems.selectedItems.projects.some(
          item => item === folder.id,
        ),
      });
    });

    // Add root tasks as individual projects if they don't belong to any folder
    const rootTasks = tasks.filter(
      task => !folders.some(folder => folder.id === task.parentId),
    );

    rootTasks.forEach(task => {
      projectsData.push({
        id: task.id,
        name: task.name,
        type: 'task',
        taskCount: 1, // Individual task counts as 1
        createdAt: task.createdAt,
        isSelected: AiSelectedItems.selectedItems.tasks.some(
          item => item === task.id,
        ),
      });
    });

    // Filter by search term
    const filteredProjects = projectsData.filter(project =>
      project.name.toLowerCase().includes(this.searchTerm),
    );

    if (filteredProjects.length > 0) {
      const projectsSection = new UIDiv();
      projectsSection.addClass('space-y-1 mb-4');
      filteredProjects.forEach(project => {
        const projectItem = new UIDiv();
        projectItem.setId(`project-item-${project.id}`);

        projectItem.addClass('flex items-center gap-3 p-3 py-2.5');

        // Selection checkbox
        const selectedItems = AiSelectedItems.selectedItems;
        const isSelected =
          project.type === 'folder'
            ? selectedItems.projects.some(item => item === project.id)
            : selectedItems.tasks.some(item => item === project.id);

        const checkbox = new UICheckbox('', isSelected, false);
        checkbox.addClass('size-[18px]');

        // Checkbox click handler for selection
        checkbox.onChange(e => {
          e.stopPropagation();
          // Check if this item can be selected (this may clear incompatible selections)
          // const canSelect = this.#canSelectItem(project.id, project.type);
          // if (!canSelect) return;

          const currentSelection = AiSelectedItems.selectedItems;

          if (project.type === 'folder') {
            // Handle project selection
            if (currentSelection.projects.includes(project.id)) {
              AiSelectedItems.setSelectedItems({
                ...currentSelection,
                projects: currentSelection.projects.filter(
                  item => item !== project.id,
                ),
              });
            } else {
              AiSelectedItems.setSelectedItems({
                ...currentSelection,
                projects: [...currentSelection.projects, project.id],
              });
            }
          } else {
            // Handle task selection
            if (currentSelection.tasks.includes(project.id)) {
              AiSelectedItems.setSelectedItems({
                ...currentSelection,
                tasks: currentSelection.tasks.filter(
                  item => item !== project.id,
                ),
              });
            } else {
              AiSelectedItems.setSelectedItems({
                ...currentSelection,
                tasks: [...currentSelection.tasks, project.id],
              });
            }
          }
          // The UI rebuild will happen automatically via AiSelectedItems subscription
        });

        projectItem.add(checkbox);

        // Project info (clickable for navigation)
        // Make projectInfo a label for the checkbox so clicking it toggles the checkbox
        const projectInfo = new UIDiv();
        projectInfo.setAttribute('for', `project-checkbox-${project.id}`);
        projectInfo.addClass('cursor-pointer');

        // Also set the checkbox id so label works
        checkbox.setId(`project-checkbox-${project.id}`);

        // Click on projectInfo toggles the checkbox
        projectInfo.onClick(e => {
          e.stopPropagation();
          checkbox.setChecked(!checkbox.getChecked());
          // Manually trigger change event for consistent state update
          checkbox.dom.dispatchEvent(new Event('change', { bubbles: true }));
        });
        projectInfo.addClass('flex-1 min-w-0 truncate');

        const projectName = new UIText(project.name);
        projectName.addClass(
          'text-sm font-medium text-slate-700 truncate block',
        );
        projectInfo.add(projectName);

        const taskCountLabel =
          project.type === 'folder'
            ? project.taskCount === null
              ? i18next.t('sidebar.aiAnalyzer.tasks_not_loaded')
              : i18next.t('sidebar.aiAnalyzer.task_count', {
                  count: project.taskCount,
                })
            : '';
        if (taskCountLabel) {
          const projectDetails = new UIText(taskCountLabel);
          projectDetails.addClass('text-xs text-slate-500 truncate block');
          projectInfo.add(projectDetails);
        }

        projectItem.add(projectInfo);

        // right arrow for navigation
        const rightArrowIcon = new IconButton({
          iconName: 'arrow-left',
          color: 'none',
          iconSize: 16,
          onClick: e => {
            e.stopPropagation();
            this.#navigateToProjectTasks(project);
          },
          buttonClass: 'rotate-180',
        });
        projectItem.add(rightArrowIcon);
        projectsSection.add(projectItem);
      });

      this.listContainer.add(projectsSection);
    } else if (filteredProjects.length === 0 && projectsData.length > 0) {
      // Show no results message if search didn't find anything
      const noResultsSection = new UIDiv();
      noResultsSection.addClass('space-y-1 mb-4');
      const noResultsMessage = new UIText(
        i18next.t('sidebar.aiAnalyzer.no_projects_found'),
      );
      noResultsMessage.addClass('text-sm text-slate-500 px-2 py-1');
      noResultsSection.add(noResultsMessage);

      this.listContainer.add(noResultsSection);
    } else {
      // Show no projects message
      const noProjectsSection = new UIDiv();
      noProjectsSection.addClass('space-y-1 mb-4');
      const noProjectsMessage = new UIText(
        i18next.t('sidebar.aiAnalyzer.no_projects_available'),
      );
      noProjectsMessage.addClass('text-sm text-slate-500 px-2 py-1');
      noProjectsSection.add(noProjectsMessage);

      this.listContainer.add(noProjectsSection);
    }
  }

  async #navigateToProjectTasks(project) {
    this.currentProject = project;
    this.projectsView = 'tasks';
    this.searchTerm = ''; // Reset search when navigating
    this.#buildUI(); // Rebuild entire UI immediately (may show loading state)

    // If tasks for this project haven't been loaded yet, fetch them now
    if (
      project.type === 'folder' &&
      !LoadedFolderIds.loadedFolderIds?.includes(project.id)
    ) {
      try {
        await ProjectsState.getProjectById(project.id);
        this.tasksLoader?.hide();
        this.#buildList();
      } catch (error) {
        console.error('Failed to load tasks for project:', error);
        this.tasksLoader?.hide();
        this.#buildList();
      }
    }
  }

  #navigateBackToProjects() {
    // AiSelectedItems.setItems([]);
    this.currentProject = null;
    this.projectsView = 'list';
    this.searchTerm = ''; // Reset search when navigating back
    this.#buildUI(); // Rebuild entire UI to update header
  }

  #buildTasksList() {
    if (!this.currentProject || !this.projects) {
      this.#navigateBackToProjects();
      return;
    }

    // Get tasks for the current project
    const allTasks = this.projects?.tasks || [];
    const projectTasks = allTasks.filter(
      task => task.parentId === this.currentProject.id,
    );

    // Show loading if tasks haven't been fetched yet for this project
    const isLoaded = LoadedFolderIds.loadedFolderIds?.includes(
      this.currentProject.id,
    );
    if (!isLoaded) {
      const loadingSection = new UIDiv();
      loadingSection.addClass('flex items-center justify-center py-6');
      this.tasksLoader = new Loader();
      loadingSection.add(this.tasksLoader);
      this.listContainer.add(loadingSection);
      return;
    }

    this.tasksLoader = null;

    // Filter by search term
    const filteredTasks = projectTasks.filter(task =>
      task.name.toLowerCase().includes(this.searchTerm),
    );

    const tasksSection = new UIDiv();
    tasksSection.addClass('space-y-1 mb-4');

    if (filteredTasks.length > 0) {
      // Task items
      filteredTasks.forEach(task => {
        const taskItem = new UIDiv();
        taskItem.setId(`task-item-${task.id}`);

        taskItem.addClass('flex items-center gap-3 p-3 py-2.5');

        // Selection checkbox
        const checkbox = new UICheckbox(
          '',
          AiSelectedItems.selectedItems.tasks.some(item => item === task.id),
          false,
        );
        checkbox.addClass('min-h-[32px]');

        // Checkbox click handler for selection
        checkbox.onChange(e => {
          e.stopPropagation();
          // Check if this task can be selected (this may clear incompatible selections)
          // const canSelect = this.#canSelectItem(task.id, 'task');
          // if (!canSelect) return;

          const currentSelection = AiSelectedItems.selectedItems;
          if (currentSelection.tasks.includes(task.id)) {
            AiSelectedItems.setSelectedItems({
              ...currentSelection,
              tasks: currentSelection.tasks.filter(item => item !== task.id),
            });
          } else {
            AiSelectedItems.setSelectedItems({
              ...currentSelection,
              tasks: [...currentSelection.tasks, task.id],
            });
          }
          // The UI rebuild will happen automatically via AiSelectedItems subscription
        });

        taskItem.add(checkbox);

        // Task info
        const taskInfo = new UIDiv();
        taskInfo.setAttribute('for', `task-checkbox-${task.id}`);
        taskInfo.addClass('cursor-pointer');

        // Also set the checkbox id so label works
        checkbox.setId(`task-checkbox-${task.id}`);

        // Click on taskInfo toggles the checkbox
        taskInfo.onClick(e => {
          e.stopPropagation();
          checkbox.setChecked(!checkbox.getChecked());
          // Manually trigger change event for consistent state update
          checkbox.dom.dispatchEvent(new Event('change', { bubbles: true }));
        });
        taskInfo.addClass('flex-1 min-w-0 truncate');

        const taskName = new UIText(task.name);
        taskName.addClass('text-sm font-medium text-slate-700 truncate block');
        taskInfo.add(taskName);

        const taskDetails = new UIText(
          `Created: ${new Date(task.createdAt).toLocaleDateString()}`,
        );
        taskDetails.addClass('text-xs text-slate-500 truncate block');
        taskInfo.add(taskDetails);

        taskItem.add(taskInfo);

        tasksSection.add(taskItem);
      });
    } else if (filteredTasks.length === 0 && projectTasks.length > 0) {
      // Show no results message if search didn't find anything
      const noResultsSection = new UIDiv();
      noResultsSection.addClass('space-y-1 mb-4');
      const noResultsMessage = new UIText(
        i18next.t('sidebar.aiAnalyzer.no_tasks_found'),
      );
      noResultsMessage.addClass('text-sm text-slate-500 px-2 py-1');
      noResultsSection.add(noResultsMessage);

      tasksSection.add(noResultsSection);
    } else {
      // Show no tasks message
      const noTasksSection = new UIDiv();
      noTasksSection.addClass('space-y-1 mb-4');
      const noTasksMessage = new UIText(
        i18next.t('sidebar.aiAnalyzer.no_tasks_in_project'),
      );
      noTasksMessage.addClass('text-sm text-slate-500 px-2 py-1');
      noTasksSection.add(noTasksMessage);

      tasksSection.add(noTasksSection);
    }

    this.listContainer.add(tasksSection);
  }

  #updateTasksSelectionCounter() {
    // Find and update the tasks selection counter
    const selectedItems = AiSelectedItems.selectedItems;
    const selectedCount =
      selectedItems.projects.length + selectedItems.tasks.length;
    const selectionText = i18next.t('sidebar.aiAnalyzer.tasks_selected', {
      count: selectedCount,
    });

    // Find the counter element in the tasks section and update it
    const counterElement = this.listContainer.find(
      '.tasks-selection-counter',
    )[0];
    if (counterElement) {
      counterElement.setTextContent(selectionText);
    }
  }
}

export { AiAnalyzerProjectsPopover };
